import { cloneElement, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_Z = 100;
const GAP = 8;

const computeFixedPosition = (placement, rect) => {
	switch (placement) {
		case 'bottom':
			return {
				left: rect.left + rect.width / 2,
				top: rect.bottom + GAP,
				transform: 'translate(-50%, 0)',
			};
		case 'left':
			return {
				left: rect.left - GAP,
				top: rect.top + rect.height / 2,
				transform: 'translate(-100%, -50%)',
			};
		case 'right':
			return {
				left: rect.right + GAP,
				top: rect.top + rect.height / 2,
				transform: 'translate(0, -50%)',
			};
		case 'top':
		default:
			return {
				left: rect.left + rect.width / 2,
				top: rect.top - GAP,
				transform: 'translate(-50%, -100%)',
			};
	}
};

const panelClasses = {
	dark: 'inline-block w-max max-w-[min(220px,calc(100vw-1rem))] rounded-lg border border-white/15 bg-black/60 px-3 py-2 text-left text-xs text-white shadow-lg shadow-black/40 backdrop-blur-sm',
	light:
		'inline-block max-w-[min(320px,calc(100vw-1rem))] rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5',
};

/**
 * Tooltip ligero sin MUI.
 *
 * - Se renderiza con `createPortal` + `position: fixed` + z-index alto para no quedar
 *   debajo de thead sticky, barra de acciones de la tabla u overflow horizontal.
 * - `variant="dark"` (default): el estilo que ya usabas (fondo oscuro semitransparente,
 *   texto blanco) — ideal para títulos cortos en string.
 * - `variant="light"`: panel claro; úsalo con `title` en JSX (p. ej. listas con text-gray).
 */
export const Tooltip = ({
	title,
	placement = 'top',
	children,
	className = '',
	disabled = false,
	variant = 'dark',
	zIndex = TOOLTIP_Z,
}) => {
	const [open, setOpen] = useState(false);
	const [fixedStyle, setFixedStyle] = useState(null);
	const id = useId();
	const wrapRef = useRef(null);

	useLayoutEffect(() => {
		if (!open || !wrapRef.current) {
			setFixedStyle(null);
			return undefined;
		}
		const update = () => {
			const el = wrapRef.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			const pos = computeFixedPosition(placement, rect);
			setFixedStyle({
				position: 'fixed',
				left: pos.left,
				top: pos.top,
				transform: pos.transform,
				zIndex,
			});
		};
		update();
		window.addEventListener('scroll', update, true);
		window.addEventListener('resize', update);
		return () => {
			window.removeEventListener('scroll', update, true);
			window.removeEventListener('resize', update);
		};
	}, [open, placement, zIndex]);

	if (!title || disabled) return children;

	const child = Array.isArray(children) ? children[0] : children;
	const isStringTitle = typeof title === 'string';

	const titleContent =
		variant === 'light' ? (
			isStringTitle ? (
				<span className='block max-w-[min(220px,calc(100vw-1rem))] truncate'>{title}</span>
			) : (
				<span className='block max-h-[min(70vh,24rem)] max-w-full overflow-y-auto overflow-x-hidden text-left'>
					{title}
				</span>
			)
		) : (
			isStringTitle ? (
				<span className='block truncate'>{title}</span>
			) : (
				<span className='block max-h-[min(70vh,24rem)] max-w-full overflow-y-auto overflow-x-hidden text-left'>
					{title}
				</span>
			)
		);

	const panelBase = variant === 'light' ? panelClasses.light : panelClasses.dark;
	const tooltipInner = (
		<span className={`${panelBase} ${className}`.trim()}>
			{titleContent}
		</span>
	);

	const triggerProps = {
		'aria-describedby': open ? id : undefined,
		onMouseEnter: () => setOpen(true),
		onMouseLeave: () => setOpen(false),
		onFocus: () => setOpen(true),
		onBlur: () => setOpen(false),
	};

	const portalTarget = typeof document !== 'undefined' ? document.body : null;

	return (
		<span ref={wrapRef} className='inline-flex'>
			{cloneElement(child, triggerProps)}
			{open && fixedStyle && portalTarget && createPortal(
				<span
					id={id}
					role='tooltip'
					style={fixedStyle}
					className='pointer-events-none'
				>
					{tooltipInner}
				</span>,
				portalTarget,
			)}
		</span>
	);
};
